import { Button, Flex, Space, Spacing } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'

import { useFieldArray, useFormContext } from '../../../../hooks'
import { DEMO_INVOICE_PRODUCTS } from '../../constants/demoInvoiceProducts'
import { useTrackDemoInvoiceEditor } from '../../hooks/useTrackDemoInvoiceEditor'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import { getDemoInvoiceRowLine } from '../../utils/getDemoInvoiceRowLine'
import { DemoInvoiceDiscounts } from '../DemoInvoiceDiscounts'
import { DemoInvoiceLine } from '../DemoInvoiceLine'
import { DemoInvoiceLinesHeader } from '../DemoInvoiceLinesHeader'
import { DemoInvoiceSummary } from '../DemoInvoiceSummary'

interface DemoInvoiceLinesProps {
  discountMode: InvoiceDiscountMode
  onDiscountModeChange: (discountMode: InvoiceDiscountMode) => void
}

export const DemoInvoiceLines = ({ discountMode, onDiscountModeChange }: DemoInvoiceLinesProps): ReactElement => {
  const { t } = useTranslation()
  const { control } = useFormContext<DemoInvoiceFormSchema>()
  const { append, fields, remove } = useFieldArray({ control, name: 'invoiceLines' })
  const { trackDemoInvoiceEditor } = useTrackDemoInvoiceEditor()

  const handleAddLineClick = useCallback(() => {
    const [defaultProduct] = DEMO_INVOICE_PRODUCTS

    trackDemoInvoiceEditor('Form - Invoice lines - Add invoice row line button clicked')
    append(getDemoInvoiceRowLine(defaultProduct, t))
  }, [append, t, trackDemoInvoiceEditor])

  return (
    <>
      <DemoInvoiceLinesHeader discountMode={discountMode} />
      {fields.map((field, lineIndex) => (
        <DemoInvoiceLine
          discountMode={discountMode}
          key={field.id}
          isRemovable={fields.length > 1}
          lineIndex={lineIndex}
          onRemove={remove}
        />
      ))}
      <Space />
      <Flex alignItems="center" gap={Spacing.S16}>
        <Button icon="circlePlus" onClick={handleAddLineClick} size="sm" variant="outlined">
          {t('external_invoices.editor.form.field.invoice_lines.add_line')}
        </Button>
        <DemoInvoiceDiscounts discountMode={discountMode} onDiscountModeChange={onDiscountModeChange} />
      </Flex>
      <Space />
      <DemoInvoiceSummary discountMode={discountMode} />
    </>
  )
}
